"use client";

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTheme } from "@/hooks/useTheme";
import { ThemeSection, ThemeText, AuroraBlobs } from "@/components/ui/theme-utils";

const QUESTIONS = [
  {
    q: "How big can a single transfer be?",
    a: "Practically unlimited. We've stress-tested multi-terabyte project bundles; the limit is your storage plan, not the wire."
  },
  {
    q: "What happens if my connection drops mid-upload?",
    a: "Zset detects the drop, resumes from the last verified packet, and continues — no manual restart, no re-uploading from zero. You can close the lid on your laptop; we'll catch up when you open it."
  },
  {
    q: "Is my data encrypted in transit and at rest?",
    a: "Yes — TLS 1.3 in transit, AES-256 at rest, and optional customer-managed KMS keys for zero-knowledge storage. Files are split into encrypted packets on the way out."
  },
  {
    q: "Can my client download without creating an account?",
    a: "Absolutely. Share a link, set the permissions, your client clicks and downloads. We generate a clean, branded download page automatically."
  },
  {
    q: "What regions do you operate in?",
    a: "We have 14 edge PoPs across North America, Europe, APAC, and the Middle East, with new ones added based on customer demand."
  }
];

export function FAQ() {
  const { isDark } = useTheme();
  const [open, setOpen] = useState<number | null>(0);

  return (
    <ThemeSection id="faq" variant="alternate" pattern="grid">
      <AuroraBlobs variant="cyan" />
      <div className="container mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          <div className="lg:col-span-4">
            <span
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium mb-5",
                isDark
                  ? "bg-white/[0.04] border border-white/10 text-white/80"
                  : "bg-white border border-stone-200 text-stone-700"
              )}
            >
              FAQ
            </span>
            <h2 className="responsive-heading-2 mb-5">
              Questions, <span className="text-gradient">answered</span>
            </h2>
            <ThemeText variant="subtle" className="responsive-text-lg mb-6">
              Couldn't find what you need? Reach out — we usually reply within a
              few hours.
            </ThemeText>
            <a
              href="mailto:hello@zset.in"
              className={cn(
                "inline-flex items-center gap-1.5 text-sm font-medium",
                isDark ? "text-brand-300 hover:text-brand-200" : "text-brand-700 hover:text-brand-800"
              )}
            >
              hello@zset.in →
            </a>
          </div>

          <div className="lg:col-span-8">
            <div
              className={cn(
                "rounded-3xl overflow-hidden",
                isDark
                  ? "bg-white/[0.02] border border-white/[0.08]"
                  : "bg-white border border-stone-200/80"
              )}
            >
              {QUESTIONS.map((item, i) => {
                const isOpen = open === i;
                return (
                  <div
                    key={item.q}
                    className={cn(
                      "transition-colors",
                      i !== 0 && (isDark ? "border-t border-white/5" : "border-t border-stone-200/80")
                    )}
                  >
                    <button
                      onClick={() => setOpen(isOpen ? null : i)}
                      className="w-full flex items-center justify-between gap-4 text-left p-6 sm:p-7"
                    >
                      <span className="text-base sm:text-lg font-semibold tracking-tight">
                        {item.q}
                      </span>
                      <span
                        className={cn(
                          "inline-flex h-8 w-8 items-center justify-center rounded-full flex-shrink-0 transition-all",
                          isOpen
                            ? "bg-gradient-to-br from-brand-500 to-brand-700 text-white"
                            : isDark
                            ? "bg-white/5 text-white/70"
                            : "bg-stone-100 text-stone-700"
                        )}
                      >
                        {isOpen ? (
                          <Minus className="h-4 w-4" />
                        ) : (
                          <Plus className="h-4 w-4" />
                        )}
                      </span>
                    </button>
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.25 }}
                          className="overflow-hidden"
                        >
                          <div className="px-6 sm:px-7 pb-6 sm:pb-7 -mt-1">
                            <ThemeText variant="subtle" className="text-sm sm:text-base leading-relaxed">
                              {item.a}
                            </ThemeText>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </ThemeSection>
  );
}
